import {SoftBody} from './soft-body.js';

// Rigid translation belongs to CollectionPile. This cage describes only local
// deformation, with independent finger constraints and contact force fields.
export class PileSoftBody {
  constructor(min,max,material='jelly'){
    this.body=new SoftBody(min,max,material,{anchorBottom:false,resolution:[4,4,4]});
    this.body.settings={...this.body.settings,preventInversion:true};
    // The small cage needs compliant edges to show contact dents. Jelly and
    // mochi keep their original volume constraints, damping and recovery.
    if(material==='jelly'||material==='mochi')this.body.settings.edge=.04;
    if(material==='dumpling')this.body.settings.edge=.055;
    if(material==='slime')Object.assign(this.body.settings,{edge:.14,volume:.0000001,damping:8,recovery:6});
    this.material=material;
    this.unit=Math.max(...max.map((n,a)=>n-min[a]));
    this.restShape=max.map((n,a)=>n-min[a]);this.restCenter=min.map((n,a)=>(n+max[a])*.5);
    this.shape=[...this.restShape];this.center=[...this.restCenter];this.proxyBindings=[];
    for(let x=-1;x<=1;x++)for(let y=-1;y<=1;y++)for(let z=-1;z<=1;z++){
      const length=Math.hypot(x,y,z);if(!length)continue;
      this.proxyBindings.push(this.body.bind([x,y,z].map((n,a)=>this.restCenter[a]+n/length*this.restShape[a]*.5)));
    }
    this.force=new Float64Array(this.body.p.length);this.previous=new Float64Array(this.body.p.length);
    this.quietTime=0;this.active=false;this.sleeping=true;
  }
  grab(point){this.body.grab(point);this.wake();}
  move(target){this.body.move(target);if(this.body.hold)this.wake();}
  release(){this.body.release();this.wake();}
  wake(){this.active=true;this.sleeping=false;this.quietTime=0;}
  step(elapsed,contacts=[]){
    if(!Number.isFinite(elapsed)||elapsed<=0)return this.active;
    const values=contacts.flatMap(c=>[...c.point,...c.normal,c.depth??0,c.load??0]);
    const changed=!this.contactSnapshot||values.length!==this.contactSnapshot.length||values.some((n,i)=>Math.abs(n-this.contactSnapshot[i])>(i%8<3?this.unit*.003:i%8<6?.005:i%8===6?this.unit*.001:.01));
    if(changed&&this.sleeping)this.wake();
    if(!this.sleeping)this.contactSnapshot=values;
    if(this.sleeping)return false;
    const dt=Math.min(elapsed,1/30),body=this.body,p=body.p;
    this.previous.set(p);this.force.fill(0);
    const radius=this.unit*(this.material==='slime'?.52:.34),radiusSquared=radius*radius;
    for(const contact of contacts){
      const {point,normal}=contact,magnitude=Math.hypot(...normal);
      if(!magnitude||![...point,...normal].every(Number.isFinite))continue;
      const load=Math.max(0,contact.load??0);
      const strength=this.unit*Math.min(16,16*(1-Math.exp(-load*1.4))+Math.max(0,contact.depth??0)/this.unit*60);
      for(let i=0;i<body.count;i++){
        const k=i*3,dx=body.initialRest[k]-point[0],dy=body.initialRest[k+1]-point[1],dz=body.initialRest[k+2]-point[2];
        const weight=Math.exp(-(dx*dx+dy*dy+dz*dz)/radiusSquared)*strength/magnitude;
        for(let a=0;a<3;a++)this.force[k+a]+=normal[a]*weight;
      }
    }
    // Remove the net force: broadphase poses already account for gravity and
    // collision translation. The remainder compresses the actual contact patch.
    const mean=[0,0,0];
    for(let k=0;k<this.force.length;k++)mean[k%3]+=this.force[k]/body.count;
    for(let k=0;k<this.force.length;k++)body.velocity[k]+=(this.force[k]-mean[k%3])*dt;
    body.step(dt);
    this.updateShape();
    let motion=0;
    for(let k=0;k<p.length;k++)motion=Math.max(motion,Math.abs(p[k]-this.previous[k]));
    this.quietTime=!body.hold&&motion<this.unit*.00008?this.quietTime+dt:0;
    if(this.quietTime>.3){
      this.active=false;this.sleeping=true;body.velocity.fill(0);body.activity=0;
    }
    return this.active;
  }
  updateShape(){
    const min=[Infinity,Infinity,Infinity],max=[-Infinity,-Infinity,-Infinity],point=[0,0,0];
    for(const binding of this.proxyBindings){
      this.body.sample(binding,point);
      for(let a=0;a<3;a++){min[a]=Math.min(min[a],point[a]);max[a]=Math.max(max[a],point[a]);}
    }
    for(let a=0;a<3;a++){this.shape[a]=max[a]-min[a];this.center[a]=(max[a]+min[a])*.5;}
  }
}
